/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `selectOptions` for the Caintra Connect frontend.
 * @module data/selectOptions
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import MEXICO_FLAG from "../assets/MexicoFlag.svg";
import USA_FLAG from "../assets/USAFlag.svg";
import FRANCE_FLAG from "../assets/FranceFlag.svg";
import CANADA_FLAG from "../assets/Canada.png";
import AccessTime from "@mui/icons-material/AccessTime";

export const getAddressTypeOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i,
    label: t(`components.location.addressTypeOption${i + 1}`),
  }));

export const getTitleOptions = (t) => {
  const titleIds = [
    44, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 378, 13, 14, 385, 383, 17, 18, 20, 21,
    22, 23, 24, 387, 386, 82, 25, 26, 27, 28, 29, 32, 33, 34, 36, 37, 38, 39,
    41, 42, 43,
  ];
  return Array.from({ length: 41 }, (_, i) => ({
    value: titleIds[i],
    label: t(`components.contactsData.titleOption${i + 1}`),
  }));
};

export const getGenderOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i + 1,
    label: t(`components.contactsData.genderOption${i + 1}`),
  }));

export const getAgeRangeOptions = (t) =>
  Array.from({ length: 6 }, (_, i) => ({
    value: i + 1,
    label: t(`pages.eventPreRegister.ageRangeOption${i + 1}`),
  }));

export const getEventPreRegisterPositionOptions = (t) =>
  Array.from({ length: 16 }, (_, i) => ({
    value: i + 1,
    label: t(`pages.eventPreRegister.positionOption${i + 1}`),
  }));

export const getAreaOptions = (t) =>
  Array.from({ length: 36 }, (_, i) => ({
    value: i + 1 >= 28 ? i + 1 + 1 : i + 1,
    label: t(`components.contactsData.areaOption${i + 1}`),
  }));

export const getOperationEnvironmentOptions = (t) =>
  Array.from({ length: 4 }, (_, i) => ({
    value: i + 1,
    label: t(`components.corporateIdentity.operationEnvironmentOption${i + 1}`),
  }));

export const getCompanySizeOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i + 1,
    label: t(`components.corporateIdentity.companySizeOption${i + 1}`),
  }));

export const getExportsOptions = (t) =>
  Array.from({ length: 2 }, (_, i) => ({
    value: i,
    label: t(`components.commercialOffer.exportsOption${i + 1}`),
  }));

export const getImportsOptions = (t) =>
  Array.from({ length: 2 }, (_, i) => ({
    value: i,
    label: t(`components.commercialOffer.importsOption${i + 1}`),
  }));

export const getSellsToGovernmentOptions = (t) =>
  Array.from({ length: 2 }, (_, i) => ({
    value: i,
    label: t(`components.commercialOffer.sellsToGovernmentOption${i + 1}`),
  }));

export const getCompanyGeneralActivityOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i + 1,
    label: t(`components.commercialOffer.companyGeneralActivityOption${i + 1}`),
  }));

export const getCertificationsOptions = (t) =>
  Array.from({ length: 26 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.certificationOption${i + 1}`),
  }));

export const getAvailableSectorsOptions = (t) =>
  Array.from({ length: 11 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.sectorOption${i + 1}`),
  }));

export const getYearsOfExperienceOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.yearsOfExperienceOption${i + 1}`),
  }));

export const getREPSEProviderOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.REPSEProviderOption${i + 1}`),
  }));

export const getAverageDeliveryTimesOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.averageDeliveryTimesOption${i + 1}`),
  }));

export const getAnnualSalesVolumeOptions = (t) =>
  Array.from({ length: 4 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.annualSalesVolumeOption${i + 1}`),
  }));

export const getAcceptedPaymentMethodsOptions = (t) =>
  Array.from({ length: 4 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.acceptedPaymentMethodsOption${i + 1}`),
  }));

export const getCivilLiabilityInsuranceOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.civilLiabilityInsuranceOption${i + 1}`),
  }));

export const getIMMEXPermitOptions = (t) =>
  Array.from({ length: 2 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.IMMEXPermitOption${i + 1}`),
  }));

export const getPracticesImplementedOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i,
    label: t(`components.certifications.practicesImplementedOption${i + 1}`),
  }));

export const getAreasOfInterestOptions = (t) =>
  Array.from({ length: 10 }, (_, i) => ({
    value: i,
    label: t(`components.areasOfInterest.areaOfInterestOption${i + 1}`),
  }));

export const getCountryExportingToOptions = (t) =>
  Array.from({ length: 8 }, (_, i) => ({
    value: i,
    label: t(`components.commercialOffer.countryExportingToOption${i + 1}`),
  }));

export const getCountryImportingFromOptions = (t) =>
  Array.from({ length: 8 }, (_, i) => ({
    value: i,
    label: t(`components.commercialOffer.countryImportingFromOption${i + 1}`),
  }));

export const getEconomicActivityOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i,
    label: t(`components.corporateIdentity.economicActivityOption${i + 1}`),
  }));

export const getSubsectorOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i,
    label: t(`components.corporateIdentity.subsectorOption${i + 1}`),
  }));

export const getBusinessGroupOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i,
    label: t(`components.corporateIdentity.businessGroupOption${i + 1}`),
  }));

export const getPositionOptions = (t) =>
  Array.from({ length: 4 }, (_, i) => ({
    value: i,
    label: t(`components.contactsData.positionOption${i + 1}`),
  }));

export const getLanguageOptions = (t) => [
  { value: "es", label: t("components.selectOptions.languageOption1"), image: MEXICO_FLAG },
  { value: "en", label: t("components.selectOptions.languageOption2"), image: USA_FLAG },
  { value: "fr", label: t("components.selectOptions.languageOption3"), image: FRANCE_FLAG },
];

export const getCountryCodeOptions = (t) => [
  { value: "MX", label: t("components.selectOptions.countryMexico"), code: "+52", image: MEXICO_FLAG },
  { value: "US", label: t("components.selectOptions.countryUnitedStates"), code: "+1", image: USA_FLAG },
  { value: "FR", label: t("components.selectOptions.countryFrance"), code: "+33", image: FRANCE_FLAG },
  { value: "CA", label: t("components.selectOptions.countryCanada"), code: "+1", image: CANADA_FLAG },
];

export const getEventTypeOptions = (t) => [
  { value: 0, label: t("components.selectOptions.eventTypeRegistration") },
  { value: 1, label: t("components.selectOptions.eventTypeBusinessMeeting") },
];

export const getEventModalityOptions = (t) => [
  { value: 0, label: t("components.selectOptions.eventModalityInPerson") },
  { value: 1, label: t("components.selectOptions.eventModalityOnline") },
];

export const getEventRegisterCategoryOptions = (t) => [
  { value: 0, label: t("components.selectOptions.eventRegisterCategory1") },
  { value: 1, label: t("components.selectOptions.eventRegisterCategory2") },
  { value: 2, label: t("components.selectOptions.eventRegisterCategory3") },
  { value: 3, label: t("components.selectOptions.eventRegisterCategory4") },
];

export const getEventBusinessCategoryOptions = (t) => [
  { value: 4, label: t("components.selectOptions.eventBusinessCategory1") },
  { value: 5, label: t("components.selectOptions.eventBusinessCategory2") },
  { value: 6, label: t("components.selectOptions.eventBusinessCategory3") },
];

export const getEventAudienceOptions = (t) => [
  { value: 0, label: t("components.selectOptions.eventAudienceMembers") },
  { value: 1, label: t("components.selectOptions.eventAudienceNonMembers") },
];

export const eventTypesOptions = (t) => [
  { value: "REGISTRATION_EVENT", label: t(`pages.reports.eventTypeOption1`) },
  { value: "BUSINESS_EVENT", label: t(`pages.reports.eventTypeOption2`) },
  { value: "B2B_EVENT", label: t(`pages.reports.eventTypeOption3`) },
  { value: "BREAKFAST_EVENT", label: t(`pages.reports.eventTypeOption4`) },
  { value: "VIRTUAL_EVENT", label: t(`pages.reports.eventTypeOption5`) },
];

export const eventTypesWithoutVirtualOptions = (t) => [
  { value: "REGISTRATION_EVENT", label: t(`pages.reports.eventTypeOption1`) },
  { value: "BUSINESS_EVENT", label: t(`pages.reports.eventTypeOption2`) },
  { value: "B2B_EVENT", label: t(`pages.reports.eventTypeOption3`) },
  { value: "BREAKFAST_EVENT", label: t(`pages.reports.eventTypeOption4`) },
];

export const getDirectoryFilters = (t) => [
  { value: "mostRecent", label: t("components.selectOptions.directoryFilterMostRecent"), icon: <AccessTime /> },
  { value: "mostSearched", label: t("components.selectOptions.directoryFilterMostSearched"), icon: <AccessTime /> },
  { value: "newest", label: t("components.selectOptions.directoryFilterNewest"), icon: <AccessTime /> },
  { value: "mostPopular", label: t("components.selectOptions.directoryFilterMostPopular"), icon: <AccessTime /> },
];

export const getStatusOptions = (t) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: i,
    label: t(`pages.viewAppointments.statusOption${i + 1}`),
  }));

export const getRequirementsOptions = (t) =>
  Array.from({ length: 2 }, (_, i) => ({
    value: i,
    label: t(`pages.viewAppointments.requirementsOption${i + 1}`),
  }));

export const getNumberOfTablesOptions = () =>
  Array.from({ length: 98 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

export const getReportsOptions = (t) =>
  Array.from({ length: 14 }, (_, i) => ({
    value: i,
    label: t(`pages.reports.reportOption${i + 1}`),
  }));

export const getNotificationTypeOptions = (t) =>
  Array.from({ length: 3 }, (_, i) => ({
    value: i,
    label: t(`pages.notifications.notificationTypeOption${i + 1}`),
  }));
